class CollectionGridRefresh {
  constructor() {
    this.storageKey = 'tmcCollectionGridSelectedMetal';
    this.validMetals = ['yellow', 'white', 'rose'];
    this.selectedMetal = this.getStoredMetal() || 'yellow';
    this.floatingFilterUpdateFrame = null;
    this.bind();
    document.addEventListener('paginate', () => this.bind());
    window.addEventListener('scroll', () => this.scheduleFloatingFilterUpdate(), { passive: true });
    window.addEventListener('resize', () => this.scheduleFloatingFilterUpdate(), { passive: true });
  }

  bind() {
    this.bindFloatingFilterButton();
    this.bindDesktopFloatingFilters();
    this.bindMetalToggles();

    document.querySelectorAll('[data-collection-grid-gallery]').forEach((gallery) => {
      if (gallery.dataset.collectionGridRefreshBound === 'true') return;
      gallery.dataset.collectionGridRefreshBound = 'true';

      const card = gallery.closest('.grid-view-item-image');
      const slides = Array.from(gallery.querySelectorAll('[data-collection-grid-gallery-slide]'));
      const progress = card?.querySelector('[data-collection-grid-gallery-progress]');
      const prev = card?.querySelector('[data-collection-grid-gallery-prev]');
      const next = card?.querySelector('[data-collection-grid-gallery-next]');
      let index = 0;
      let startX = null;

      const setIndex = (nextIndex) => {
        if (!slides.length) return;
        index = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
        });
        if (progress) progress.style.transform = `scaleX(${(index + 1) / slides.length})`;
      };
      gallery.collectionGridSetIndex = setIndex;

      prev?.addEventListener('click', (event) => {
        event.preventDefault();
        setIndex(index - 1);
      });

      next?.addEventListener('click', (event) => {
        event.preventDefault();
        setIndex(index + 1);
      });

      gallery.addEventListener('touchstart', (event) => {
        startX = event.touches[0]?.clientX ?? null;
      }, { passive: true });

      gallery.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const endX = event.changedTouches[0]?.clientX ?? startX;
        const delta = endX - startX;
        startX = null;
        if (Math.abs(delta) < 30) return;
        setIndex(delta < 0 ? index + 1 : index - 1);
      }, { passive: true });

      setIndex(0);
    });

    this.updateMetalImages();
  }

  bindMetalToggles() {
    document.querySelectorAll('[data-collection-grid-metal]').forEach((button) => {
      if (button.dataset.collectionGridMetalBound === 'true') return;
      button.dataset.collectionGridMetalBound = 'true';

      button.addEventListener('click', () => {
        this.selectedMetal = this.normaliseMetal(button.dataset.collectionGridMetal) || 'yellow';
        this.storeSelectedMetal();
        this.updateMetalImages();
      });
    });
  }

  normaliseMetal(metal) {
    return this.validMetals.includes(metal) ? metal : null;
  }

  getStoredMetal() {
    try {
      return this.normaliseMetal(window.sessionStorage.getItem(this.storageKey));
    } catch (error) {
      return null;
    }
  }

  storeSelectedMetal() {
    try {
      window.sessionStorage.setItem(this.storageKey, this.selectedMetal);
    } catch (error) {
      // Storage can be unavailable in some private browsing contexts.
    }
  }

  updateMetalImages() {
    this.selectedMetal = this.getStoredMetal() || this.selectedMetal || 'yellow';

    document.querySelectorAll('[data-collection-grid-metal]').forEach((button) => {
      const isActive = button.dataset.collectionGridMetal === this.selectedMetal;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    document.querySelectorAll('[data-collection-grid-gallery]').forEach((gallery) => {
      const metalSlide = gallery.querySelector('[data-collection-grid-metal-slide]');
      const image = metalSlide?.querySelector('img');
      if (!image) return;

      if (!image.dataset.collectionGridOriginalImage) {
        image.dataset.collectionGridOriginalImage = image.currentSrc || image.src;
      }

      const metalKey = `metal${this.selectedMetal[0].toUpperCase()}${this.selectedMetal.slice(1)}Image`;
      const selectedImage = gallery.dataset[metalKey];
      const nextImage = selectedImage || gallery.dataset.metalYellowImage || image.dataset.collectionGridOriginalImage;

      image.src = nextImage;
      image.removeAttribute('srcset');
      gallery.collectionGridSetIndex?.(0);
    });
  }

  bindFloatingFilterButton() {
    const button = document.querySelector('.collection_grid .mobile-facets__open');
    const isReadyToPropose = Boolean(document.querySelector('.collection_grid .facets__form-inner.is-ready-to-propose'));
    if (isReadyToPropose) {
      button?.classList.remove('is-floating');
      return;
    }

    if (!button || button.dataset.collectionGridFloatingBound === 'true') {
      this.updateFloatingFilterButton();
      return;
    }

    button.dataset.collectionGridFloatingBound = 'true';
    const marker = document.createElement('span');
    marker.className = 'collection-grid-refresh__filter-marker';
    marker.setAttribute('aria-hidden', 'true');
    button.before(marker);
    this.updateFloatingFilterButton();
  }

  bindDesktopFloatingFilters() {
    const filters = document.querySelector('.collection_grid collection-filtering-form');
    if (filters?.querySelector('.facets__form-inner.is-ready-to-propose')) {
      filters.classList.remove('is-floating-desktop');
      return;
    }

    if (!filters || filters.dataset.collectionGridDesktopFloatingBound === 'true') {
      this.updateFloatingFilterButton();
      return;
    }

    filters.dataset.collectionGridDesktopFloatingBound = 'true';
    const marker = document.createElement('span');
    marker.className = 'collection-grid-refresh__desktop-filter-marker';
    marker.setAttribute('aria-hidden', 'true');
    filters.before(marker);
    this.updateFloatingFilterButton();
  }

  scheduleFloatingFilterUpdate() {
    if (this.floatingFilterUpdateFrame !== null) return;

    this.floatingFilterUpdateFrame = window.requestAnimationFrame(() => {
      this.floatingFilterUpdateFrame = null;
      this.updateFloatingFilterButton();
    });
  }

  updateFloatingFilterButton() {
    const collection = document.querySelector('.collection_grid');
    const button = collection?.querySelector('.mobile-facets__open');
    const marker = collection?.querySelector('.collection-grid-refresh__filter-marker');
    const desktopFilters = collection?.querySelector('collection-filtering-form');
    const desktopMarker = collection?.querySelector('.collection-grid-refresh__desktop-filter-marker');
    if (!collection) return;

    const isReadyToPropose = Boolean(collection.querySelector('.facets__form-inner.is-ready-to-propose'));
    if (isReadyToPropose) {
      button?.classList.remove('is-floating');
      desktopFilters?.classList.remove('is-floating-desktop');
      if (desktopMarker) desktopMarker.style.height = '';
      return;
    }

    if (window.innerWidth < 768) {
      desktopFilters?.classList.remove('is-floating-desktop');
      if (desktopMarker) desktopMarker.style.height = '';

      if (!button || !marker) return;
      const markerRect = marker.getBoundingClientRect();
      const collectionRect = collection.getBoundingClientRect();
      const shouldFloat = markerRect.bottom < 0 && collectionRect.bottom > 68;
      button.classList.toggle('is-floating', shouldFloat);
      return;
    }

    button?.classList.remove('is-floating');
    if (!desktopFilters || !desktopMarker) return;

    const markerRect = desktopMarker.getBoundingClientRect();
    const collectionRect = collection.getBoundingClientRect();
    const filterHeight = desktopFilters.getBoundingClientRect().height;
    const shouldFloat = markerRect.top < 0 && collectionRect.bottom > filterHeight + 32;

    if (shouldFloat && !desktopFilters.classList.contains('is-floating-desktop')) {
      desktopMarker.style.height = `${filterHeight}px`;
    } else if (!shouldFloat) {
      desktopMarker.style.height = '';
    }

    desktopFilters.classList.toggle('is-floating-desktop', shouldFloat);
  }
}

new CollectionGridRefresh();
